import { useCallback, useEffect, useRef, useState } from 'react'

type IdItem = { id: string }

type UsePaginatedFetchOptions<T extends IdItem> = {
  fetchPage: (page: number) => Promise<T[]>
  initialPage?: number
  limit?: number
}

function usePaginatedFetch<T extends IdItem>({
  fetchPage,
  limit = 999,
}: UsePaginatedFetchOptions<T>) {
  const [items, setItems] = useState<T[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadingRef = useRef(false)
  const pageRef = useRef(0)

  const loadMore = useCallback(async () => {
    if (loadingRef.current || !hasMore) return

    loadingRef.current = true
    setError(null)

    if (pageRef.current === 0) {
      setLoading(true)
    } else {
      setLoadingMore(true)
    }

    try {
      const newItems = await fetchPage(pageRef.current)

      setItems(prev => {
        const existingIds = new Set(prev.map(item => item.id))
        const filtered = newItems.filter(item => !existingIds.has(item.id))
        return [...prev, ...filtered]
      })

      // TODO: It is better to have a items count from request.
      if (newItems.length < limit) setHasMore(false)

      pageRef.current += 1
    } catch (err: any) {
      setError(err.message || 'Unknown error')
    } finally {
      loadingRef.current = false
      setLoadingMore(false)
      setLoading(false)
    }
  }, [fetchPage, hasMore, limit])

  useEffect(() => {
    if (pageRef.current !== 0) return
    void loadMore()
  }, [loadMore])

  useEffect(() => {
    pageRef.current = 0
    setItems([])
    setHasMore(true)
    setError(null)
  }, [fetchPage])

  return {
    items,
    loading,
    error,
    hasMore,
    loadMore,
    loadingMore,
  }
}

export default usePaginatedFetch
